import { orderBy } from "lodash";
import { IAggregatPeriod } from "~app/common/models/aggregatperiod";
import { IDagKvantitet } from "~app/common/models/dagkvantitet";
import { DefaultForsorjningsrad, ForsorjningskallaAggregat, ForsorjningskallaTyp, IForsorjningskalla } from "~app/common/models/forsorjningskalla";
import { LeveransraderAggregat } from "~app/common/models/leveransrad";
import { TaktCalculator } from "~app/common/models/takt-calculator";
import { IKundorderDetailKundorder, IKundorderDetailKundorderrad } from "../../../models/kundorder-detail-model";

export interface ITaktSettings {
    totaltAntalArbetsdagarInnevarandeManad: number;
    passeradeAntalArbetsdagarInnevarandeManad: number;
    showTakt: boolean;
}

export class KundorderradAggregat {

    get kundorderradId(): number {
        return this.kor.id;
    }

    get overenskommet(): number {
        return this.kor.overenskommenKvantitetLeveransrad;
    }

    set overenskommet(value: number) {
        this.kor.overenskommenKvantitetLeveransrad = value;
    }

    get periodFrom(): Date {
        return this.kor.periodFrom;
    }

    get periodTom(): Date {
        return this.kor.periodTom;
    }

    get mattslag(): string {
        return this.ko?.mattslag;
    }

    get handelssortimentId(): number {
        return this.kor.handelssortiment.id;
    }

    get manuellPaArbetsorder(): boolean {
        return this.kor.manuellPaArbetsorder;
    }

    readonly forsorjningskallor: ForsorjningskallaAggregat[];

    private readonly leveransraderAggregat: LeveransraderAggregat;

    constructor(
        private readonly ko: IKundorderDetailKundorder,
        private readonly kor: IKundorderDetailKundorderrad) {
        this.leveransraderAggregat = new LeveransraderAggregat(kor.leveransrader);

        const typOrder = [
            ForsorjningskallaTyp.Produktionsomrade,
            ForsorjningskallaTyp.Terminal,
            ForsorjningskallaTyp.Leverantorsorder
        ];

        this.forsorjningskallor = orderBy(
            ForsorjningskallaAggregat.createMany(kor.forsorjningskallor, this.leveransraderAggregat),
            [
                x => typOrder.indexOf(x.typ),
                x => x.namn
            ]
        );
    }

    sumForsorjningsrader(period: IAggregatPeriod) {
        return this.leveransraderAggregat.sumForsorjningsrad(period);
    }

    sumLeveransrad = (period: IAggregatPeriod): number => this.leveransraderAggregat.sumLeveransrad(period);

    sumTransporterat = (period: IAggregatPeriod): number => this.leveransraderAggregat.sumTransporteratLeveransrad(period);

    sumAvtalat = (period: IAggregatPeriod): number => this.leveransraderAggregat.sumAvtalatLeveransrad(period);

    sumTransporteratForsorjningskallor = (period: IAggregatPeriod, forsorjningskallorIdn?: number[]): number =>
        this.leveransraderAggregat.sumTransporterat(period, forsorjningskallorIdn);

    sumPlan = (period: IAggregatPeriod): number => this.forsorjningskallor.reduce((agg, item) => agg + item.sumPlan(period), 0);

    sumBalans = (period: IAggregatPeriod): number => {
        if (!period.isCurrentlyActive) {
            return this.sumTransporterat(period) - this.sumLeveransrad(period);
        }

        return this.sumPlan(period) - this.sumLeveransrad(period);
    };

    sumTransporteratLeveransrad = (periods: IAggregatPeriod[]): number =>
        periods.reduce((agg, item) => agg + (item.isCurrentlyActive ? this.sumLeveransrad(item) : this.sumTransporterat(item)), 0);

    calculateTakt = (period: IAggregatPeriod, passeradeArbetsdagar: number, antalArbetsdagarInnevarandeManad: number): number =>
        TaktCalculator.calculate(period, this.sumPlan, this.sumTransporterat, passeradeArbetsdagar, antalArbetsdagarInnevarandeManad);

    avvikelse = (periods: IAggregatPeriod[]): number => this.sumTransporteratLeveransrad(periods) - this.overenskommet;

    calculateTaktLeverans = (period: IAggregatPeriod, passeradeArbetsdagar: number, antalArbetsdagarInnevarandeManad: number): number =>
        TaktCalculator.calculate(period, this.sumLeveransrad, this.sumTransporterat, passeradeArbetsdagar, antalArbetsdagarInnevarandeManad);

    updateAvtaladKvantitet(fordeladKvantitet: IDagKvantitet[]): void {
        this.leveransraderAggregat.updateAvtaladKvantitet(fordeladKvantitet);
    }

    updateLeveransradKvantitet(fordeladKvantitet: IDagKvantitet[]): void {
        this.leveransraderAggregat.updateLeveransradKvantitet(fordeladKvantitet);
    }

    hasForsorjningskalla = (raw: IForsorjningskalla): boolean => this.forsorjningskallor.some(x => x.id === raw.id && x.typ === raw.group);

    addForsorjningskalla(forsorjningskalla: IForsorjningskalla, fordeladKvantitet: IDagKvantitet[]): ForsorjningskallaAggregat {
        if (this.hasForsorjningskalla(forsorjningskalla)) {
            return null;
        }
        const forsorjningsrader = fordeladKvantitet
            .map(x => new DefaultForsorjningsrad(forsorjningskalla.id, forsorjningskalla.typ, x.datum, x.kvantitet));

        const aggregat = new ForsorjningskallaAggregat(forsorjningskalla, forsorjningsrader);

        this.forsorjningskallor.push(aggregat);
        return aggregat;
    }

    removeForsorjningskalla(forsorjningskalla: ForsorjningskallaAggregat): void {
        const idx = this.forsorjningskallor.indexOf(forsorjningskalla);
        this.forsorjningskallor.splice(idx, 1);

        this.leveransraderAggregat.removeForsorjningskalla(forsorjningskalla);

        const rawIdx = this.kor.forsorjningskallor.findIndex(x => x.id === forsorjningskalla.id && x.group === forsorjningskalla.typ);
        this.kor.forsorjningskallor.splice(rawIdx, 1);
    }
}
